/**
 * @description Hook utilizado para realizar operações relacionadas com os cadáveres
 * @author Diego Sabino Amorim de Araújo
 * @file useCadaverService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import { useDispatch } from 'react-redux'
import actionTypes from 'utils/actionTypes'
import { useFetch } from './useFetch'

export const useCadaverService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const listarCadaver = async (id, setCadaver, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/cadaver/${id}`)
      setCadaver(response.data.cadaver)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCadaverAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/cadaver/${id}`)
      return response.data.cadaver
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const excluirCadaver = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.del(`/cadaver/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarCadaver = async (dados, idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosCadastro = {
        nic: dados.nic,
        id_caso: idCaso,
        descricaoGeral: dados.descricaoGeral,
        id_endereco_origem: dados.endOrig ? dados.endOrig.id : null,
        id_usuario_coleta: dados.usuCol ? dados.usuCol.id : null,
        usuarioColetaInput: dados.usuarioColetaInput,
        id_usuario_transporte: dados.usuTrans ? dados.usuTrans.id : null,
        usuarioTransporteInput: dados.usuarioTransporteInput,
        dataColeta: dados.dataCol,
        dataTransporte: dados.dataTrans,
        id_necroterio: dados.necroterio ? dados.necroterio.id : null,
        arquivos: dados.arquivos,
        causaJuridicaMortePreliminar: dados.causaJuridicaMortePreliminar,
      }
      const response = await fetch.postOpen(`/cadaver/`, { dadosCadastro })
      return response.data.cadaver
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarCadaver = async (id, idCaso, dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dadosEdicao = {
        nic: dados.nic,
        id_caso: idCaso,
        descricaoGeral: dados.descricaoGeral,
        id_endereco_origem: dados.endOrig ? dados.endOrig.id : null,
        id_usuario_coleta: dados.usuCol ? dados.usuCol.id : null,
        usuarioColetaInput: dados.usuarioColetaInput,
        id_usuario_transporte: dados.usuTrans ? dados.usuTrans.id : null,
        usuarioTransporteInput: dados.usuarioTransporteInput,
        dataColeta: dados.dataCol,
        dataTransporte: dados.dataTrans,
        id_necroterio: dados.necroterio ? dados.necroterio.id : null,
        arquivos: dados.arquivos,
        causaJuridicaMortePreliminar: dados.causaJuridicaMortePreliminar,
      }
      await fetch.putOpen(`/cadaver/${id}`, { dadosEdicao })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const definirCausaMorte = async (id, idCausaMorte, causaMorteTextoLivre, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        id_causa_morte: idCausaMorte,
        causaMorteTextoLivre,
      }
      await fetch.putOpen(`cadaver/definircausamorte/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const liberarCadaver = async (id, setCadaver, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.put(`/cadaver/liberar/${id}`)
      await listarCadaver(id, setCadaver, setProcessando)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const reconhecerCadaver = async (dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.post(`/cadaver/reconhecer`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const entregarCadaver = async (dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.post(`/cadaver/entregar`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const getFenomenosCadavericosCadaver = async (idExame, idCadaver, setFenomenosCadavericos, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/exameNecroscopico/fenomenosCadavericos/${idExame}/${idCadaver}`)
      setFenomenosCadavericos(response.data.fenomenosCadavericos)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarFenomenoCadaverico = async (idExame, idCadaver, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        id_cadaver: idCadaver,
        id_exame_realizado: idExame,
        id_tipo: values.tipo ? values.tipo.id : null,
        tipoTextoLivre: values.tipoTextoLivre,
        observacoes: values.observacoes,
      }
      await fetch.post(`exameNecroscopico/fenomenoCadaverico`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }
  const excluirFenomenoCadaverico = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.del(`exameNecroscopico/fenomenoCadaverico/${id}`)
    if (setProcessando) setProcessando(false)
  }

  const getLesoesCadaver = async (idExame, idCadaver, setLesoes, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/exameNecroscopico/lesoes/${idExame}/${idCadaver}`)
      const lesoesOrdenadas = (response.data.lesoes || []).slice().sort((a, b) => (a.id || 0) - (b.id || 0))
      setLesoes(lesoesOrdenadas)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const getLesaoCadaver = async (id, setLesao, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`exameNecroscopico/lesao/${id}`)
      setLesao(response.data.lesao)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCicatrizesCadaverAsync = async (idExame, idCadaver, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`exameNecroscopico/cicatrizes/${idExame}/${idCadaver}`)
      return response.data.cicatrizes
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarLesaoCadaver = async (idExame, idCadaver, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        id_cadaver: idCadaver,
        id_exame_realizado: idExame,
        id_tipo: values.tipo ? values.tipo.id : null,
        regioes: values.regioes ? values.regioes.map((regiao) => regiao.id) : null,
        regioesSaida: values.regioesSaida ? values.regioesSaida.map((regiao) => regiao.id) : null,
        ...values,
      }
      await fetch.post(`exameNecroscopico/lesao`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const getAnalisesInternasCadaver = async (idExame, idCadaver, setAnalisesInternas, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`exameNecroscopico/analisesInternas/${idExame}/${idCadaver}`)
      setAnalisesInternas(response.data.analisesInternas)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarAnaliseInternaCadaver = async (idExame, idCadaver, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = { ...values, id_cadaver: idCadaver, id_exame_realizado: idExame }
      await fetch.post(`exameNecroscopico/analiseInterna`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(true)
    }
  }

  const excluirAnaliseInternaCadaver = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.del(`exameNecroscopico/analiseInterna/${id}`)
    if (setProcessando) setProcessando(false)
  }

  const editarLesaoCadaver = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        id_tipo: values.tipo ? values.tipo.id : null,
        regioes: values.regioes ? values.regioes.map((regiao) => regiao.id) : null,
        regioesSaida: values.regioesSaida ? values.regioesSaida.map((regiao) => regiao.id) : null,
        ...values,
      }

      await fetch.put(`exameNecroscopico/lesao/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const excluirLesaoCadaver = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.del(`exameNecroscopico/lesao/${id}`)
    if (setProcessando) setProcessando(false)
  }

  const getCadaveresDeLocalParaExame = async (idLocal, idExame, setCadaveres, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/cadaver/deLocal/paraExame/${idLocal}/${idExame}`)
      setCadaveres(response.data.cadaveres)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const getCadaveresDeCaso = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`cadaver/deCaso/${idCaso}`)
      dispatch({
        type: actionTypes.SET_CADAVERES_CASO,
        cadaveres: response.data.cadaveres,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCadaveresDeCasoAsync = async (idCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`cadaver/deCaso/${idCaso}`)
      return response.data.cadaveres
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const setarLesoesCadaver = async (id, idExameRealizado, lesoes, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        idExameRealizado,
        lesoes: lesoes.map((l) => ({
          ...l,
          id_tipo: l.tipo ? l.tipo.id : null,
        })),
      }

      await fetch.putOpen(`cadaver/lesoes/setar/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const setarCicatrizesCadaver = async (id, idExameRealizado, cicatrizes, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)

      const dados = {
        idExameRealizado,
        cicatrizes: cicatrizes.map((c) => ({
          ...c,
        })),
      }

      await fetch.putOpen(`cadaver/cicatrizes/setar/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarEContarCadaveresPorNicAsync = async (nic, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`cadaver/pornic/${nic}`)
      return response.data.listagem
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCadaveresFiltradosAsync = async (dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const filtros = {
        necroterios: dados.necroterios ? dados.necroterios.map((necroterio) => necroterio.id) : null,
        status: dados.status && dados.status.length ? dados.status : null,
        classificacao: dados.classificacao && dados.classificacao.length ? dados.classificacao : null,
        cidades: dados.cidades ? dados.cidades.map((cidade) => cidade.nome) : null,
        dataEntradaInicio: dados.dataEntradaChecked && dados.dataEntradaInicio ? dados.dataEntradaInicio : null,
        dataEntradaFinal: dados.dataEntradaChecked && dados.dataEntradaFinal ? dados.dataEntradaFinal : null,
        causasMortes: dados.causaMorte ? dados.causaMorte.map((morte) => morte.id) : null,
        resultadoExame: dados.resultadoExame && dados.resultadoExame.length ? dados.resultadoExame : null,
      }

      const response = await fetch.postOpenWithoutFeedback('cadaver/filtrar/', filtros)
      return response.data.cadaveres
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCadaveresLikeStringAsync = async (string, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`cadaver/filtrar/porstring/${string}`)
      return response.data.cadaveres
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCadaveresRotaPublicaDiaAtualAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`arquivo/cadaveres/public/listagem/hoje`)
      return response.data.cadaveres
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCadaveresRotaPublicaDiaAnteriorAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`arquivo/cadaveres/public/listagem/ontem`)
      return response.data.cadaveres
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const openPDFInNewTab = (url, nome) => {
    window.open(url, nome)
  }

  const downloadTermoEntrega = async (idCadaver, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.postOpenWithOptions(
        `/cadaver/download/termoentrega/${idCadaver}`,
        { ...values },
        {
          responseType: 'blob',
        }
      )

      const fileURL = URL.createObjectURL(response.data)
      openPDFInNewTab(fileURL, 'Termo de entrega')
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const inumarCadaver = async (dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.post(`/cadaver/inumar`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const exumarCadaver = async (dados, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.post(`/cadaver/exumar`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    listarCadaver,
    listarCadaverAsync,
    excluirCadaver,
    cadastrarCadaver,
    editarCadaver,
    getFenomenosCadavericosCadaver,
    cadastrarFenomenoCadaverico,
    excluirFenomenoCadaverico,
    getLesoesCadaver,
    getLesaoCadaver,
    cadastrarLesaoCadaver,
    editarLesaoCadaver,
    excluirLesaoCadaver,
    getAnalisesInternasCadaver,
    cadastrarAnaliseInternaCadaver,
    excluirAnaliseInternaCadaver,
    getCadaveresDeLocalParaExame,
    getCadaveresDeCaso,
    listarCadaveresDeCasoAsync,
    liberarCadaver,
    reconhecerCadaver,
    entregarCadaver,
    setarLesoesCadaver,
    setarCicatrizesCadaver,
    listarEContarCadaveresPorNicAsync,
    listarCadaveresFiltradosAsync,
    listarCadaveresLikeStringAsync,
    listarCicatrizesCadaverAsync,
    listarCadaveresRotaPublicaDiaAtualAsync,
    listarCadaveresRotaPublicaDiaAnteriorAsync,
    downloadTermoEntrega,
    definirCausaMorte,
    inumarCadaver,
    exumarCadaver,
  }
}
